import React, { useState } from "react";
import LoadingOverlay from "./LoadingOverlay";
import {
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { buildPageItems } from "../utils/listPageUtils";

export default function DataListPage({
  title,
  rows = [],
  columns = [],
  count = 0,
  page = 0,
  rowsPerPage = 10,
  rowsPerPageOptions = [10, 25, 100],
  onPageChange,
  onRowsPerPageChange,
  createButton,
  headerActions = [],
  search,
  filters = [],
  toolbarExtra,
  onView,
  menuActions = [],
  loading = false,
  getRowKey = (row, index) => row.id ?? index,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [searchInput, setSearchInput] = useState(search?.value ?? "");

  const showActions = Boolean(onView || menuActions.length);
  const totalPages = Math.max(Math.ceil(count / rowsPerPage), 1);
  const displayRows =
    rows.length > rowsPerPage
      ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : rows;
  const start = count === 0 ? 0 : page * rowsPerPage + 1;
  const end =
    count === 0 ? 0 : Math.min(page * rowsPerPage + displayRows.length, count);
  const pageItems = buildPageItems(page, totalPages);

  const runSearch = () => {
    if (search?.onSearch) {
      search.onSearch(searchInput.trim());
    }
  };

  const handleMenuOpen = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setMenuRow(row);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  if (loading) {
    return <LoadingOverlay message="Loading data..." />;
  }

  return (
    <div className="data-list-page">
      <div className="data-list-top">
        <h1 className="data-list-title">{title}</h1>
        {(headerActions.length > 0 || createButton) && (
          <div className="data-list-top-actions">
            {headerActions.map((action) => (
              <button
                key={action.label}
                type="button"
                className="data-list-secondary-btn"
                onClick={action.onClick}
                disabled={action.disabled}
              >
                {action.label}
              </button>
            ))}
            {createButton && (
              <button
                type="button"
                className="data-list-create-btn"
                onClick={createButton.onClick}
              >
                {createButton.label || "Create"}
              </button>
            )}
          </div>
        )}
      </div>

      {(search || filters.length > 0 || toolbarExtra) && (
        <div className="data-list-toolbar">
          {search && (
            <div className="data-list-search">
              <SearchIcon className="data-list-search-icon" />
              <input
                type="text"
                placeholder={search.placeholder}
                value={searchInput}
                onChange={(event) => {
                  const value = event.target.value;
                  setSearchInput(value);
                  if (search.live && search.onSearch) {
                    search.onSearch(value.trim());
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    runSearch();
                  }
                }}
              />
            </div>
          )}

          {toolbarExtra}

          {filters.length > 0 && (
            <div className="data-list-filters">
              {filters.map((filter) => (
                <select
                  key={filter.id}
                  className="data-list-select"
                  value={filter.value}
                  onChange={(event) => filter.onChange(event.target.value)}
                >
                  {filter.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ))}
              <button type="button" className="data-list-filter-btn">
                <FilterListIcon fontSize="small" />
                Filter
              </button>
            </div>
          )}

          {search && !filters.length && search.showFilterButton !== false && (
            <button
              type="button"
              className="data-list-filter-btn"
              onClick={runSearch}
            >
              <FilterListIcon fontSize="small" />
              Search
            </button>
          )}
        </div>
      )}

      <div className="data-list-card">
        <div className="data-list-table-wrap">
          <table className="data-list-table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.id} style={{ width: column.width }}>
                    {column.label}
                  </th>
                ))}
                {showActions && (
                  <th className="data-list-th-action">Action</th>
                )}
              </tr>
            </thead>
            <tbody>
              {displayRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length + (showActions ? 1 : 0)}
                    className="data-list-empty"
                  >
                    No results found
                  </td>
                </tr>
              ) : (
                displayRows.map((row, index) => (
                  <tr key={getRowKey(row, index)}>
                    {columns.map((column) => (
                      <td key={column.id}>
                        {column.render
                          ? column.render(row)
                          : row[column.field]}
                      </td>
                    ))}
                    {showActions && (
                      <td className="data-list-td-action">
                        {onView && (
                          <IconButton
                            size="small"
                            className="data-list-icon-btn"
                            onClick={() => onView(row)}
                            aria-label="View"
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        )}
                        {menuActions.length > 0 && (
                          <IconButton
                            size="small"
                            className="data-list-icon-btn"
                            onClick={(event) => handleMenuOpen(event, row)}
                            aria-label="More actions"
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="data-list-pagination">
          <span className="data-list-pagination-info">
            Showing {start} to {end} of {count} results
          </span>
          <div className="data-list-pagination-controls">
            <button
              type="button"
              className="data-list-page-btn"
              disabled={page === 0}
              onClick={() => onPageChange(null, page - 1)}
              aria-label="Previous page"
            >
              <ChevronLeftIcon fontSize="small" />
            </button>

            {pageItems.map((item, index) =>
              item === "ellipsis" ? (
                <span key={`ellipsis-${index}`} className="data-list-ellipsis">
                  ...
                </span>
              ) : (
                <button
                  key={item}
                  type="button"
                  className={`data-list-page-btn ${
                    item === page ? "is-active" : ""
                  }`}
                  onClick={() => onPageChange(null, item)}
                >
                  {item + 1}
                </button>
              )
            )}

            <button
              type="button"
              className="data-list-page-btn"
              disabled={page >= totalPages - 1}
              onClick={() => onPageChange(null, page + 1)}
              aria-label="Next page"
            >
              <ChevronRightIcon fontSize="small" />
            </button>

            <select
              className="data-list-page-size"
              value={rowsPerPage}
              onChange={onRowsPerPageChange}
            >
              {rowsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option} / page
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {menuActions.map((action) => (
          <MenuItem
            key={action.label}
            onClick={() => {
              action.onClick(menuRow);
              handleMenuClose();
            }}
          >
            {action.label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}
